import { Icon } from "@iconify/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../Shared/Loading";
import SuperAdminAllRessellerTable from "./SuperAdminAllRessellerTable";
import SuperAdminRessellerModal from "./SuperAdminModal/SuperAdminRessellerModal";
import SuperAdminAddUserModal from "./SuperAdminModal/SuperAdminAddUserModal";
import { useQuery } from "../../Hooks/useApi";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

/* ─── Trends Area Chart ────────────────────────────────────────────── */
const RenderTrendsChart = ({ trends, activePeriod, onPeriodChange }) => {
  const labels = trends?.labels || [];
  const userData = trends?.user || [];
  const doctorData = trends?.doctor || [];
  const adminData = trends?.admin || [];

  const chartData = labels.map((label, idx) => ({
    name: label,
    Resellers: adminData[idx] || 0,
    Doctors: doctorData[idx] || 0,
    Patients: userData[idx] || 0,
  }));

  const legend = [
    { label: "Resellers", color: "#06b6d4" },
    { label: "Doctors", color: "#818cf8" },
    { label: "Patients", color: "#34d399" },
  ];

  const periodBtns = ["All", "6m", "3m", "1m"];

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm flex-1 min-w-0 overflow-hidden">
      <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Growth Analytics</p>
          <h3 className="text-[15px] font-black text-slate-800 mt-0.5 tracking-tight">Platform Growth Timeline</h3>
        </div>
        <div className="flex items-center gap-1.5 bg-slate-50 p-1 rounded-xl border border-slate-100">
          {periodBtns.map((p) => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all duration-200 ${activePeriod === p
                  ? "bg-white text-slate-800 shadow-sm border border-slate-200"
                  : "text-slate-500 hover:text-slate-700"
                }`}
            >
              {p === "All" ? "All time" : p.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-wrap gap-x-5 gap-y-2 mb-5">
          {legend.map((l) => (
            <span key={l.label} className="flex items-center gap-2 text-[11px] font-semibold text-slate-500">
              <span className="w-3 h-1.5 rounded-full" style={{ backgroundColor: l.color }} />
              {l.label}
            </span>
          ))}
        </div>

        {chartData.length === 0 ? (
          <div className="h-[220px] flex flex-col items-center justify-center text-slate-300">
            <Icon icon="solar:chart-bold-duotone" className="text-5xl mb-3" />
            <p className="text-[12px] font-medium text-slate-400">No data for this period</p>
          </div>
        ) : (
          <div className="w-full h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <defs>
                  <linearGradient id="gResellers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gDoctors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#818cf8" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#818cf8" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gPatients" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.18} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 600 }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fill: "#94a3b8", fontSize: 10, fontWeight: 600 }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    borderRadius: "12px",
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                    fontFamily: "inherit",
                    fontSize: "12px",
                  }}
                  labelStyle={{ color: "#1e293b", fontWeight: 800, marginBottom: "4px" }}
                  itemStyle={{ fontWeight: 700, padding: "2px 0" }}
                />
                <Area type="monotone" dataKey="Resellers" stroke="#06b6d4" strokeWidth={2.5} fill="url(#gResellers)" dot={false} />
                <Area type="monotone" dataKey="Doctors" stroke="#818cf8" strokeWidth={2.5} fill="url(#gDoctors)" dot={false} />
                <Area type="monotone" dataKey="Patients" stroke="#34d399" strokeWidth={2.5} fill="url(#gPatients)" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Order Status Donut Chart ─────────────────────────────────────── */
const OrderStatusWidget = ({ breakdown }) => {
  const pending = breakdown?.pending || 0;
  const confirm = breakdown?.confirm || 0;
  const reject = breakdown?.reject || 0;
  const total = pending + confirm + reject;

  const donutData = [
    { name: "Pending", value: pending, color: "#f59e0b" },
    { name: "Confirmed", value: confirm, color: "#10b981" },
    { name: "Rejected", value: reject, color: "#f43f5e" },
  ].filter((d) => d.value > 0);

  const isAllZero = total === 0;
  const finalData = isAllZero ? [{ name: "No Orders", value: 1, color: "#e2e8f0" }] : donutData;
  const pct = (val) => (total > 0 ? Math.round((val / total) * 100) : 0);

  const rows = [
    { label: "Pending Approval", value: pending, pct: pct(pending), dot: "bg-amber-400", bg: "bg-amber-50", text: "text-amber-700" },
    { label: "Confirmed", value: confirm, pct: pct(confirm), dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700" },
    { label: "Rejected", value: reject, pct: pct(reject), dot: "bg-rose-500", bg: "bg-rose-50", text: "text-rose-700" },
  ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm lg:w-72 flex-shrink-0 overflow-hidden">
      <div className="px-6 pt-5 pb-4 border-b border-slate-100">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Orders</p>
        <h3 className="text-[15px] font-black text-slate-800 mt-0.5 tracking-tight">Verification Status</h3>
      </div>
      <div className="p-6">
        <div className="relative w-36 h-36 mx-auto mb-5">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={finalData} cx="50%" cy="50%" innerRadius={42} outerRadius={58} paddingAngle={isAllZero ? 0 : 5} dataKey="value" strokeWidth={0}>
                {finalData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              {!isAllZero && (
                <Tooltip
                  contentStyle={{ background: "#fff", borderRadius: "10px", border: "1px solid #e2e8f0", fontSize: "11px", fontWeight: 700, fontFamily: "inherit" }}
                />
              )}
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <p className="text-2xl font-black text-slate-800 leading-none">{total}</p>
            <p className="text-[10px] font-semibold text-slate-400 mt-1 uppercase tracking-wider">Total</p>
          </div>
        </div>
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.label} className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl ${r.bg}`}>
              <div className="flex items-center gap-2.5">
                <span className={`w-2 h-2 rounded-full ${r.dot} flex-shrink-0`} />
                <span className="text-[12px] font-semibold text-slate-700">{r.label}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-[12px] font-black ${r.text}`}>{r.value}</span>
                <span className="text-[10px] text-slate-400 font-semibold">({r.pct}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── KPI Card ─────────────────────────────────────────────────────── */
const KpiCard = ({ label, value, icon, bg, glow, shine }) => (
  <div
    className="relative rounded-2xl p-6 overflow-hidden cursor-default transition-all duration-300 hover:-translate-y-1.5 hover:shadow-2xl"
    style={{ background: bg, boxShadow: `0 4px 22px -4px ${glow}` }}
  >
    {/* Decorative circles */}
    <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full" style={{ background: "rgba(255,255,255,0.07)" }} />
    <div className="absolute -bottom-8 -left-6 w-32 h-32 rounded-full" style={{ background: "rgba(255,255,255,0.05)" }} />

    {/* Dot grid */}
    <div
      className="absolute inset-0 opacity-[0.08]"
      style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "18px 18px" }}
    />

    {/* Top shine line */}
    <div className="absolute top-0 inset-x-0 h-px" style={{ background: shine }} />

    <div className="relative z-10 flex items-center gap-5">
      {/* Glass icon */}
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.22)", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}
      >
        <Icon icon={icon} className="text-white text-[26px]" />
      </div>

      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-widest leading-none" style={{ color: "rgba(255,255,255,0.58)" }}>
          {label}
        </p>
        <p className="text-[30px] font-black text-white tracking-tight leading-tight mt-1.5">{value}</p>
      </div>
    </div>
  </div>
);

/* ─── Feed Item ─────────────────────────────────────────────────────── */
const FeedItem = ({ avatar, name, sub, badge, badgeColor, meta }) => (
  <div className="flex items-center justify-between p-3.5 rounded-xl hover:bg-slate-50 transition-colors border border-transparent hover:border-slate-100">
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600 font-black flex items-center justify-center text-sm flex-shrink-0">
        {avatar}
      </div>
      <div className="min-w-0">
        <p className="text-[13px] font-bold text-slate-800 truncate leading-tight">{name}</p>
        <p className="text-[11px] text-slate-400 font-medium truncate mt-0.5">{sub}</p>
      </div>
    </div>
    <div className="text-right flex flex-col items-end gap-1.5 flex-shrink-0 ml-3">
      <span className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full ${badgeColor}`}>{badge}</span>
      {meta && <p className="text-[10px] text-slate-400 font-medium">{meta}</p>}
    </div>
  </div>
);

/* ─── Section Label ─────────────────────────────────────────────────── */
const SectionLabel = ({ icon, title, loading = false }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2">
      <Icon icon={icon} className="text-slate-400 text-[16px]" />
      <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.14em]">{title}</span>
    </div>
    {loading && (
      <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 border border-cyan-500 border-t-transparent rounded-full animate-spin" />
        Refreshing
      </span>
    )}
  </div>
);

/* ─── Custom Date Picker ────────────────────────────────────────────── */
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS_SHORT = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const CustomDatePicker = ({ value, onChange, min, max, placeholder = "Select date" }) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => {
    if (value) return new Date(value + "T00:00:00").getFullYear();
    return new Date().getFullYear();
  });
  const [viewMonth, setViewMonth] = useState(() => {
    if (value) return new Date(value + "T00:00:00").getMonth();
    return new Date().getMonth();
  });
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (value) {
      const d = new Date(value + "T00:00:00");
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [value]);

  const firstWeekDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = Array(firstWeekDay).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const toStr = (d) => `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const todayStr = new Date().toISOString().slice(0, 10);

  const isDisabled = (d) => {
    const s = toStr(d);
    if (min && s < min) return true;
    if (max && s > max) return true;
    return false;
  };

  const displayValue = value
    ? new Date(value + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : null;

  const prevMonth = () => viewMonth === 0 ? (setViewMonth(11), setViewYear(y => y - 1)) : setViewMonth(m => m - 1);
  const nextMonth = () => viewMonth === 11 ? (setViewMonth(0), setViewYear(y => y + 1)) : setViewMonth(m => m + 1);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2.5 px-3.5 py-2.5 w-[168px] rounded-xl border text-[12px] font-semibold transition-all ${open
            ? "bg-white border-indigo-400 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]"
            : value
              ? "bg-white border-slate-200 hover:border-indigo-300"
              : "bg-slate-50 border-slate-200 hover:bg-white hover:border-slate-300"
          }`}
      >
        <Icon icon="solar:calendar-bold-duotone" className={`text-[15px] flex-shrink-0 ${value ? "text-indigo-400" : "text-slate-400"}`} />
        <span className={`flex-1 text-left truncate ${value ? "text-slate-700" : "text-slate-400"}`}>
          {displayValue || placeholder}
        </span>
        <Icon icon={open ? "solar:alt-arrow-up-bold" : "solar:alt-arrow-down-bold"} className="text-slate-300 text-[11px] flex-shrink-0" />
      </button>

      {/* Dropdown calendar */}
      {open && (
        <div className="absolute top-[calc(100%+6px)] left-0 z-[999] bg-white rounded-2xl border border-slate-200/80 w-[272px] overflow-hidden"
          style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.13), 0 2px 8px rgba(0,0,0,0.06)" }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3.5 border-b border-slate-100">
            <button onClick={prevMonth} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors">
              <Icon icon="solar:alt-arrow-left-bold" className="text-sm" />
            </button>
            <span className="text-[13px] font-black text-slate-800">
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 transition-colors">
              <Icon icon="solar:alt-arrow-right-bold" className="text-sm" />
            </button>
          </div>

          {/* Grid */}
          <div className="px-3 pt-3 pb-3.5">
            {/* Weekday labels */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS_SHORT.map(d => (
                <div key={d} className="text-center text-[9px] font-black text-slate-400 uppercase py-1 tracking-wider">{d}</div>
              ))}
            </div>
            {/* Day cells */}
            <div className="grid grid-cols-7">
              {cells.map((d, i) => {
                if (!d) return <div key={`_${i}`} />;
                const s = toStr(d);
                const selected = s === value;
                const disabled = isDisabled(d);
                const isToday = s === todayStr;
                return (
                  <button
                    key={d}
                    disabled={disabled}
                    onClick={() => { onChange(s); setOpen(false); }}
                    className={`relative flex items-center justify-center h-9 w-full rounded-xl text-[12px] font-bold transition-all duration-150
                      ${selected ? "text-white shadow-sm scale-105"
                        : disabled ? "text-slate-200 cursor-not-allowed"
                          : isToday ? "text-indigo-600 font-black bg-indigo-50"
                            : "text-slate-700 hover:bg-indigo-50 hover:text-indigo-600"
                      }`}
                    style={selected ? { background: "linear-gradient(135deg,#6366f1,#7c3aed)" } : {}}
                  >
                    {d}
                    {isToday && !selected && (
                      <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-400" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer — clear */}
          {value && (
            <div className="border-t border-slate-100 px-3 py-2.5">
              <button
                onClick={() => { onChange(""); setOpen(false); }}
                className="w-full text-[11px] font-bold text-slate-400 hover:text-rose-500 transition-colors text-center py-1"
              >
                Clear date
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Filter Bar ────────────────────────────────────────────────────── */
const FilterBar = ({ period, onPeriodChange, customRange, onApplyCustom, isFetching, filterInfo }) => {
  const [localFrom, setLocalFrom] = useState(customRange.start || "");
  const [localTo, setLocalTo] = useState(customRange.end || "");

  const isCustomActive = !!(customRange.start && customRange.end);
  const hasAnyFilter = isCustomActive || period !== "All";

  const pills = [
    { key: "All", label: "All Time" },
    { key: "1m", label: "1 Month" },
    { key: "3m", label: "3 Months" },
    { key: "6m", label: "6 Months" },
  ];

  return (
    <div className="relative bg-white rounded-2xl shadow-[0_1px_16px_rgba(0,0,0,0.06)] border border-slate-200/60">


      <div className="px-5 py-4 pt-5">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3">

          {/* ── Section label ── */}
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md flex-shrink-0"
              style={{ background: "linear-gradient(135deg,#0f172a,#1e293b)" }}>
              <Icon icon="solar:calendar-date-bold-duotone" className="text-white text-[18px]" />
            </div>
            {/* <div>
              <p className="text-[13px] font-black text-slate-800 leading-tight">Date Range Filter</p>
              <p className="text-[10px] text-slate-400 font-medium mt-0.5">Narrow dashboard data by period</p>
            </div> */}
          </div>

          {/* Separator */}
          <div className="h-9 w-px bg-slate-100 hidden lg:block" />

          {/* ── Period pills ── */}
          <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-xl border border-slate-100">
            {pills.map(({ key, label }) => {
              const active = period === key && !isCustomActive;
              return (
                <button
                  key={key}
                  onClick={() => { onPeriodChange(key); setLocalFrom(""); setLocalTo(""); }}
                  disabled={isFetching}
                  className={`px-4 py-2 rounded-lg text-[11px] font-bold transition-all duration-200 whitespace-nowrap disabled:opacity-50 ${active
                      ? "text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-800 hover:bg-white"
                    }`}
                  style={active ? { background: "linear-gradient(135deg,#1e293b,#0f172a)" } : {}}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Separator */}
          <div className="h-9 w-px bg-slate-100 hidden lg:block" />

          {/* ── Custom date pickers ── */}
          <div className="flex flex-wrap items-center gap-2.5">
            {/* From */}
            <CustomDatePicker
              value={localFrom}
              onChange={setLocalFrom}
              max={localTo || undefined}
              placeholder="From date"
            />

            {/* Arrow connector */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="w-3 h-px bg-slate-200" />
              <Icon icon="solar:arrow-right-bold" className="text-[11px] text-slate-400" />
              <div className="w-3 h-px bg-slate-200" />
            </div>

            {/* To */}
            <CustomDatePicker
              value={localTo}
              onChange={setLocalTo}
              min={localFrom || undefined}
              placeholder="To date"
            />

            {/* Apply */}
            <button
              onClick={() => localFrom && localTo && onApplyCustom(localFrom, localTo)}
              disabled={!localFrom || !localTo || isFetching}
              className="px-5 py-2.5 text-white text-[12px] font-bold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-px"
              style={{ background: "linear-gradient(135deg,#6366f1,#7c3aed)", boxShadow: "0 4px 14px rgba(99,102,241,0.35)" }}
            >
              Apply Range
            </button>

            {/* Clear */}
            {hasAnyFilter && (
              <button
                onClick={() => { setLocalFrom(""); setLocalTo(""); onPeriodChange("All"); }}
                className="flex items-center gap-1.5 px-3.5 py-2.5 text-[11px] font-bold text-slate-500 hover:text-rose-600 rounded-xl border border-slate-100 hover:border-rose-200 hover:bg-rose-50 transition-all"
              >
                <Icon icon="solar:close-circle-bold-duotone" className="text-sm" />
                Reset
              </button>
            )}
          </div>

          {/* ── Right: status pill ── */}
          <div className="ml-auto flex items-center">
            {isFetching ? (
              <div className="flex items-center gap-2 text-[11px] font-semibold text-indigo-600 bg-indigo-50 border border-indigo-100 px-3.5 py-2 rounded-xl">
                <div className="w-3.5 h-3.5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                Updating data…
              </div>
            ) : hasAnyFilter &&
            <div className="flex items-center gap-2 text-[11px] font-bold text-violet-700 bg-violet-50 border border-violet-100 px-3.5 py-2 rounded-xl">
              <Icon icon="solar:filter-bold-duotone" className="text-sm text-violet-500" />
              {filterInfo}
            </div>
            }
          </div>

        </div>
      </div>
    </div>
  );
};

/* ─── Main Dashboard ───────────────────────────────────────────────── */
const SuperAdminDashboardIndex = () => {
  const [modalIsOpenAddResseller, setModalIsOpenAddResseller] = useState(false);
  const [isOpenAddUser, setIsOpenAddUser] = useState(false);

  // Filter state
  const [period, setPeriod] = useState("All");
  const [customRange, setCustomRange] = useState({ start: "", end: "" });

  // Build dynamic API URL — React Query re-fetches automatically when it changes
  const statsUrl = useMemo(() => {
    if (customRange.start && customRange.end) {
      return `/user/dashboard-stats?startDate=${customRange.start}&endDate=${customRange.end}`;
    }
    if (period !== "All") {
      return `/user/dashboard-stats?period=${period}`;
    }
    return "/user/dashboard-stats";
  }, [period, customRange]);

  const { data, loading, isFetching } = useQuery(statsUrl);

  const stats = data?.data || {
    counts: { admin: 0, doctor: 0, user: 0, totalProducts: 0, totalOrders: 0, totalRevenue: 0 },
    orderBreakdown: { pending: 0, confirm: 0, reject: 0 },
    recentUsers: [],
    recentOrders: [],
    trends: { labels: [], user: [], doctor: [], admin: [] },
    filter: null,
  };

  const handlePeriodChange = (p) => {
    setPeriod(p);
    setCustomRange({ start: "", end: "" });
  };

  const handleApplyCustom = (start, end) => {
    setCustomRange({ start, end });
    setPeriod("All");
  };

  const getFilterInfo = () => {
    if (customRange.start && customRange.end) {
      return `${new Date(customRange.start).toLocaleDateString()} – ${new Date(customRange.end).toLocaleDateString()}`;
    }
    if (period !== "All") {
      const labels = { "1m": "Last 1 month", "3m": "Last 3 months", "6m": "Last 6 months" };
      return labels[period] || "";
    }
    return "All time data";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loading />
      </div>
    );
  }

  const todayStr = new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const kpis = [
    {
      label: "Total Resellers",
      value: stats.counts.admin.toLocaleString(),
      icon: "solar:users-group-two-rounded-bold-duotone",
      bg: "linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #4f46e5 100%)",
      glow: "rgba(37,99,235,0.38)",
      shine: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0) 100%)",
    },
    {
      label: "Total Doctors",
      value: stats.counts.doctor.toLocaleString(),
      icon: "solar:medical-kit-bold-duotone",
      bg: "linear-gradient(135deg, #a855f7 0%, #7c3aed 55%, #5b21b6 100%)",
      glow: "rgba(124,58,237,0.38)",
      shine: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)",
    },
    {
      label: "Total Patients",
      value: stats.counts.user.toLocaleString(),
      icon: "solar:user-heart-bold-duotone",
      bg: "linear-gradient(135deg, #34d399 0%, #059669 55%, #0891b2 100%)",
      glow: "rgba(5,150,105,0.38)",
      shine: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.32) 50%, rgba(255,255,255,0) 100%)",
    },
    {
      label: "Total Products",
      value: stats.counts.totalProducts.toLocaleString(),
      icon: "solar:box-bold-duotone",
      bg: "linear-gradient(135deg, #fbbf24 0%, #d97706 55%, #b45309 100%)",
      glow: "rgba(217,119,6,0.40)",
      shine: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.32) 50%, rgba(255,255,255,0) 100%)",
    },
    {
      label: "Total Orders",
      value: stats.counts.totalOrders.toLocaleString(),
      icon: "solar:cart-large-bold-duotone",
      bg: "linear-gradient(135deg, #f43f5e 0%, #db2777 55%, #9d174d 100%)",
      glow: "rgba(219,39,119,0.38)",
      shine: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.28) 50%, rgba(255,255,255,0) 100%)",
    },
    {
      label: "Total Revenue",
      value: `$${stats.counts.totalRevenue.toLocaleString()}`,
      icon: "solar:dollar-minimalistic-bold-duotone",
      bg: "linear-gradient(135deg, #1e3a5f 0%, #0f172a 55%, #020617 100%)",
      glow: "rgba(15,23,42,0.55)",
      shine: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.18) 50%, rgba(255,255,255,0) 100%)",
    },
  ];

  return (
    <div className="space-y-6 select-none">

      {/* ── Page Header ─────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">{todayStr}</span>
          </div>
          <h1 className="text-[22px] font-black text-slate-900 tracking-tight leading-none">
            Dashboard Overview
          </h1>
          <p className="text-[12px] text-slate-400 font-medium mt-1">
            Monitor and manage your platform from one place.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setModalIsOpenAddResseller(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-white text-[12px] font-bold rounded-xl transition-all hover:-translate-y-px hover:shadow-lg"
            style={{ background: "linear-gradient(135deg,#0ea5e9,#2563eb)", boxShadow: "0 4px 14px rgba(37,99,235,0.3)" }}
          >
            <Icon icon="solar:user-plus-bold" className="text-[15px]" />
            Add Reseller
          </button>
          <button
            onClick={() => setIsOpenAddUser(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-[12px] font-bold rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all hover:-translate-y-px"
          >
            <Icon icon="solar:user-plus-bold" className="text-[15px] text-slate-500" />
            Add Patient
          </button>
        </div>
      </div>

      {/* ── Filter Bar ───────────────────────────────────────────────── */}
      <FilterBar
        period={period}
        onPeriodChange={handlePeriodChange}
        customRange={customRange}
        onCustomRange={setCustomRange}
        onApplyCustom={handleApplyCustom}
        isFetching={isFetching}
        filterInfo={getFilterInfo()}
      />

      {/* ── KPI Cards ────────────────────────────────────────────────── */}
      <div>
        <SectionLabel icon="solar:chart-bold-duotone" title="Platform Statistics" loading={isFetching} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
          {kpis.map((kpi, i) => <KpiCard key={i} {...kpi} />)}
        </div>
      </div>

      {/* ── Charts ───────────────────────────────────────────────────── */}
      <div>
        <SectionLabel icon="solar:graph-bold-duotone" title="Visual Analytics" />
        <div className="flex flex-col lg:flex-row gap-4 mt-3">
          <RenderTrendsChart
            trends={stats.trends}
            activePeriod={customRange.start ? "custom" : period}
            onPeriodChange={handlePeriodChange}
          />
          <OrderStatusWidget breakdown={stats.orderBreakdown} />
        </div>
      </div>

      {/* ── Activity Feeds ───────────────────────────────────────────── */}
      <div>
        <SectionLabel icon="solar:pulse-bold-duotone" title="Activity Feed" />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mt-3">

          {/* Recent Registrations */}
          <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#06b6d4,#0ea5e9)" }}>
                  <Icon icon="solar:users-group-two-rounded-bold-duotone" className="text-white text-[15px]" />
                </div>
                <div>
                  <p className="text-[13px] font-black text-slate-800 leading-tight">New Signups</p>
                  <p className="text-[10px] text-slate-400 font-medium">Recent registrations</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </span>
            </div>
            <div className="p-3">
              {stats.recentUsers.length > 0 ? (
                stats.recentUsers.map((u, i) => {
                  let badgeColor = "bg-slate-100 text-slate-600";
                  let roleName = u.role;
                  if (u.role === "admin") { badgeColor = "bg-purple-100 text-purple-700"; roleName = "Reseller"; }
                  else if (u.role === "doctor") { badgeColor = "bg-blue-100 text-blue-700"; roleName = "Doctor"; }
                  else if (u.role === "user") { badgeColor = "bg-emerald-100 text-emerald-700"; roleName = "Patient"; }
                  return (
                    <FeedItem
                      key={i}
                      avatar={(u.name || "U").substring(0, 1).toUpperCase()}
                      name={u.name || "Unknown User"}
                      sub={u.email}
                      badge={roleName}
                      badgeColor={badgeColor}
                      meta={u.createdAt ? new Date(u.createdAt).toLocaleDateString() : ""}
                    />
                  );
                })
              ) : (
                <div className="py-10 text-center">
                  <Icon icon="solar:users-group-two-rounded-bold-duotone" className="text-slate-200 text-4xl mx-auto mb-2" />
                  <p className="text-[12px] text-slate-400 font-medium">No signups in this period</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Orders */}
          <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#818cf8,#6366f1)" }}>
                  <Icon icon="solar:cart-large-bold-duotone" className="text-white text-[15px]" />
                </div>
                <div>
                  <p className="text-[13px] font-black text-slate-800 leading-tight">Latest Orders</p>
                  <p className="text-[10px] text-slate-400 font-medium">Recent transactions</p>
                </div>
              </div>
              <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Live
              </span>
            </div>
            <div className="p-3">
              {stats.recentOrders.length > 0 ? (
                stats.recentOrders.map((o, i) => {
                  let statusColor = "bg-amber-100 text-amber-700";
                  let statusText = "Pending";
                  if (o.status === "Confirm") { statusColor = "bg-emerald-100 text-emerald-700"; statusText = "Confirmed"; }
                  else if (o.status === "Reject") { statusColor = "bg-rose-100 text-rose-700"; statusText = "Rejected"; }
                  const name = o.firstName ? `${o.firstName} ${o.lastName || ""}`.trim() : (o.name || "Customer");
                  return (
                    <FeedItem
                      key={i}
                      avatar={<Icon icon="solar:bag-bold-duotone" className="text-indigo-400 text-base" />}
                      name={name}
                      sub={o.emailAddress || o.email || "No email"}
                      badge={statusText}
                      badgeColor={statusColor}
                      meta={`$${(o.total || 0).toLocaleString()}`}
                    />
                  );
                })
              ) : (
                <div className="py-10 text-center">
                  <Icon icon="solar:cart-large-bold-duotone" className="text-slate-200 text-4xl mx-auto mb-2" />
                  <p className="text-[12px] text-slate-400 font-medium">No orders in this period</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Resellers Table ──────────────────────────────────────────── */}
      <div>
        <SectionLabel icon="solar:users-group-two-rounded-bold-duotone" title="Reseller Management" />
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm overflow-hidden mt-3">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "linear-gradient(135deg,#a855f7,#7c3aed)" }}>
              <Icon icon="solar:users-group-two-rounded-bold-duotone" className="text-white text-[15px]" />
            </div>
            <div>
              <p className="text-[13px] font-black text-slate-800 leading-tight">Active Resellers</p>
              <p className="text-[10px] text-slate-400 font-medium">Manage your reseller network</p>
            </div>
          </div>
          <div className="p-4 md:p-6">
            <SuperAdminAllRessellerTable />
          </div>
        </div>
      </div>

      <SuperAdminRessellerModal isOpen={modalIsOpenAddResseller} closeModal={() => setModalIsOpenAddResseller(false)} />
      <SuperAdminAddUserModal isOpen={isOpenAddUser} closeModal={() => setIsOpenAddUser(false)} />
    </div>
  );
};

export default SuperAdminDashboardIndex;
